import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { loadSinglePublicProfile } from "@/services/publicProfile.service";
import type { PublicProfile } from "@/components/profile/PublicProfileCard";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ArrowLeft,
  MapPin,
  Briefcase,
  Star,
  Languages,
  DollarSign,
  Loader2,
} from "lucide-react";

export default function PublicProfileView() {
  const { uid } = useParams<{ uid: string }>();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const lang = (language as "en" | "fr") || "en";

  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!uid) return;
    setLoading(true);
    loadSinglePublicProfile(uid)
      .then((p) => {
        if (!p) setNotFound(true);
        else setProfile(p);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [uid]);

  const roleLabel: Record<string, { en: string; fr: string }> = {
    broker: { en: "Broker", fr: "Courtier" },
    consultant: { en: "Consultant", fr: "Consultant" },
    partner: { en: "Internal Partner", fr: "Partenaire Interne" },
    buyer: { en: "Buyer", fr: "Acheteur" },
    seller: { en: "Seller", fr: "Vendeur" },
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="container mx-auto p-6 max-w-3xl space-y-4 text-center">
        <p className="text-muted-foreground py-12">
          {lang === "fr" ? "Profil introuvable." : "Profile not found."}
        </p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />
          {lang === "fr" ? "Retour" : "Go back"}
        </Button>
      </div>
    );
  }

  const displayName = `${profile.firstName} ${profile.lastName}`.trim();
  const initials =
    `${profile.firstName?.[0] || ""}${profile.lastName?.[0] || ""}`.toUpperCase() ||
    "?";
  const location = [profile.city, profile.stateProvince, profile.country]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="container mx-auto p-6 max-w-3xl space-y-4">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4" />
        {lang === "fr" ? "Retour" : "Back"}
      </Button>

      <Card>
        <CardHeader className="flex flex-row items-start gap-4">
          <Avatar className="h-20 w-20">
            <AvatarImage src={profile.photoURL} alt={displayName} />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0 space-y-1">
            <CardTitle className="text-2xl">{displayName}</CardTitle>
            <Badge variant="secondary">
              {roleLabel[profile.role]?.[lang] || profile.role}
            </Badge>
            {profile.firmName && (
              <p className="text-sm text-muted-foreground">{profile.firmName}</p>
            )}
            {profile.companyName && !profile.firmName && (
              <p className="text-sm text-muted-foreground">
                {profile.companyName}
              </p>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            {location && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-4 w-4 shrink-0" />
                {location}
              </div>
            )}
            {profile.primarySector && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Briefcase className="h-4 w-4 shrink-0" />
                {profile.primarySector}
              </div>
            )}
            {profile.yearsExperience && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Star className="h-4 w-4 shrink-0" />
                {profile.yearsExperience}{" "}
                {lang === "fr" ? "ans d'expérience" : "years of experience"}
              </div>
            )}
            {Array.isArray(profile.spokenLanguages) &&
              profile.spokenLanguages.length > 0 && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Languages className="h-4 w-4 shrink-0" />
                  {profile.spokenLanguages.join(", ")}
                </div>
              )}
            {(profile as any).hourlyRate && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <DollarSign className="h-4 w-4 shrink-0" />${" "}
                {(profile as any).hourlyRate}
                {lang === "fr" ? " / heure" : " / hour"}
              </div>
            )}
          </div>

          {profile.bio && (
            <div className="space-y-2">
              <h3 className="font-semibold">
                {lang === "fr" ? "À propos" : "About"}
              </h3>
              <p className="text-sm text-muted-foreground whitespace-pre-line">
                {profile.bio}
              </p>
            </div>
          )}

          {Array.isArray(profile.feeStructure) &&
            profile.feeStructure.length > 0 && (
              <div className="space-y-2">
                <h3 className="font-semibold">
                  {lang === "fr"
                    ? "Structure des honoraires"
                    : "Fee Structure"}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {profile.feeStructure.map((f) => (
                    <Badge key={f} variant="outline">
                      {f}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

          {Array.isArray(profile.addons) && profile.addons.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-semibold">
                {lang === "fr" ? "Services actifs" : "Active Services"}
              </h3>
              <div className="flex flex-wrap gap-2">
                {profile.addons.map((a) => (
                  <Badge key={a} variant="outline">
                    {a.replace(/_/g, " ")}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <div className="pt-2">
            <Button
              className="w-full"
              onClick={() => navigate(`/messages?to=${uid}`)}
            >
              {lang === "fr" ? "Contacter" : "Contact"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
